import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { TrendingUp, CreditCard, DollarSign, Activity } from 'lucide-react';
import { toast } from 'sonner';
import AxiosDB from '@/config/axios2';


const Billing = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      const response = await AxiosDB.get(`/dashboard/summary`);
      setSummary(response.data);
    } catch (error) {
      toast.error('Error al cargar el resumen');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]">Cargando...</div>;
  }

  const stats = [
    {
      title: 'Suscripciones Activas',
      value: summary?.active_subscriptions || 0,
      total: summary?.total_subscriptions || 0,
      icon: Activity,
      color: 'from-green-500 to-emerald-600',
      testId: 'active-subscriptions'
    },
    {
      title: 'Costo Mensual',
      value: Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(summary?.total_monthly_cost || 0),
      icon: CreditCard,
      color: 'from-blue-500 to-cyan-600',
      testId: 'monthly-cost'
    },
    {
      title: 'Costo Anual',
      value: Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(summary?.total_yearly_cost || 0),
      icon: TrendingUp,
      color: 'from-purple-500 to-pink-600',
      testId: 'yearly-cost'
    },
    {
      title: 'Total Gastado',
      value: Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(summary?.total_spent || 0),
      icon: DollarSign,
      color: 'from-orange-500 to-red-600',
      testId: 'total-spent'
    },
  ];

  return (
    <div className="space-y-6" data-testid="billing-dashboard">
      <div>
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Dashboard de Facturación
        </h2>
        <p className="text-gray-600">Resumen de tus suscripciones y pagos</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="overflow-hidden border-0 shadow-lg hover:shadow-xl" data-testid={stat.testId}>
              <div className={`h-2 bg-gradient-to-r ${stat.color}`} />
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    {stat.title}
                  </CardTitle>
                  <div className={`p-2 rounded-lg bg-gradient-to-r ${stat.color}`}>
                    <Icon className="h-4 w-4 text-white" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {stat.value}
                  {stat.total !== undefined && (
                    <span className="text-sm text-gray-500 ml-2">/ {stat.total}</span>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Payments */}
      <Card className="shadow-lg" data-testid="recent-payments">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Pagos Recientes</CardTitle>
          <CardDescription>Tus últimas 5 transacciones</CardDescription>
        </CardHeader>
        <CardContent>
          {!summary?.recent_payments || summary.recent_payments.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No hay pagos recientes</p>
          ) : (
            <div className="space-y-4">
              {summary.recent_payments.map((payment) => (
                <div
                  key={payment.id}
                  data-testid={`payment-${payment.id}`}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
                >
                  <div className="flex-1">
                    <p className="font-medium">{payment.description}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(payment.date).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {payment.divisa} {
                        Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(payment.amount || 0)
                      }
                    </p>
                    <Badge
                      variant={payment.status === 'Pagado' ? 'default' : 'destructive'}
                      className={payment.status === 'Pagado'
                        ? 'bg-green-100 text-green-800 hover:bg-green-100'
                        : 'bg-red-100 text-red-800 hover:bg-red-100'
                      }
                    >
                      {payment.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Billing;